import { type QueryClient } from 'react-query'

import { useNotificationStore } from 'store/notificationsCacheStore' // adjust path

type LikeableType = 'notification' | 'post' | 'story' | string

type ToggleLikeCacheArgs = {
  likeableType: LikeableType
  likeableId: number
  nextLiked: boolean

  // only needed for zustand notifications store
  notifFilterKey?: string
}

type Snapshot = Array<{ key: unknown; data: unknown }>

function patchInfinitePages(old: any, patchItem: (item: any) => any) {
  if (!old?.pages || !Array.isArray(old.pages)) return old

  return {
    ...old,
    pages: old.pages.map((p: any) => {
      const list = p?.page?.data?.data
      if (!Array.isArray(list)) return p
      return {
        ...p,
        page: {
          ...p.page,
          data: {
            ...p.page.data,
            data: list.map(patchItem)
          }
        }
      }
    })
  }
}

function patchNotificationItem(item: any, id: number, nextLiked: boolean) {
  if (!item || typeof item !== 'object') return item
  if ((item).id !== id) return item
  return { ...item, is_liked: nextLiked }
}

/**
 * Updates both:
 * - React Query cache (all ['notifications', *] infinite queries)
 * - Zustand notification-cache for notifFilterKey (first-page cache)
 *
 * Returns snapshot for rollback.
 */
export function toggleLikeInCaches(
  queryClient: QueryClient,
  args: ToggleLikeCacheArgs
): { snapshot: Snapshot } {
  const { likeableType, likeableId, nextLiked, notifFilterKey } = args

  const snapshot: Snapshot = []

  // --- React Query: notifications infinite queries ---
  if (likeableType === 'notification') {
    const notifQueries = queryClient.getQueryCache().findAll(['notifications'])

    for (const q of notifQueries) {
      const key = q.queryKey
      const prev = queryClient.getQueryData(key)
      snapshot.push({ key, data: prev })

      queryClient.setQueryData(key, (old: any) =>
        patchInfinitePages(old, (item: any) =>
          patchNotificationItem(item, likeableId, nextLiked)
        )
      )
    }
  }

  // --- Zustand: persisted notification-cache (by filter key) ---
  if (likeableType === 'notification' && notifFilterKey) {
    const store = useNotificationStore.getState()
    const prev = store.getCache(notifFilterKey)

    // snapshot zustand too (store it under a synthetic key)
    snapshot.push({ key: ['zustand:notification-cache', notifFilterKey], data: prev })

    if (Array.isArray(prev)) {
      store.setCache(
        notifFilterKey,
        prev.map(n => (n?.id === likeableId ? { ...n, is_liked: nextLiked } : n))
      )
    }
  }

  return { snapshot }
}

/** Rollback both caches using snapshot returned by toggleLikeInCaches */
export function rollbackToggleLikeInCaches(
  queryClient: QueryClient,
  snapshot: Snapshot
) {
  for (const snap of snapshot) {
    // zustand snapshots are tagged with a synthetic key
    if (Array.isArray(snap.key) && snap.key[0] === 'zustand:notification-cache') {
      const filterKey = snap.key[1] as string
      useNotificationStore.getState().setCache(filterKey, (snap.data as any[]) ?? [])
      continue
    }

    queryClient.setQueryData(snap.key as any, snap.data)
  }
}